import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "./ui/PageHeader";
import { ArrowLeft, ArrowUpRight } from "./ui/Icons";

const blogPosts = [
  {
    id: 1,
    title: "AI Action Summit in Paris",
    excerpt:
      "A transformative experience at the AI Action Summit in Paris, where global leaders and innovators gathered to shape the future of artificial intelligence.",
    image: "https://hyphentech.africa/static/media/yan.8c4f66e7de581de44d74.jpeg",
    content: `
      <p>From February 10–11, 2025, I had the privilege of participating in the AI Action Summit in Paris, a truly transformative experience where global leaders, AI pioneers, and innovators came together to shape the future of artificial intelligence.</p>
      <h2>Day One: Grand Palais</h2>
      <p>The summit took place at the Grand Palais on February 10, and I had the honor of meeting some of the most influential figures in AI and technology, including:</p>
      <ul>
        <li>Yann LeCun, Chief AI Scientist at Meta</li>
        <li>Sundar Pichai, CEO of Google</li>
        <li>Arthur Mensch, CEO of Mistral AI</li>
        <li>Alexandr Wang, CEO of Scale AI</li>
      </ul>
      <p>President Emmanuel Macron and other ministers shared their vision for AI's role in shaping our societies. During his keynote, Sundar Pichai described this era as the 'Golden Age of Innovation', emphasizing how AI will redefine how future generations live.</p>
      <h2>Key Takeaways</h2>
      <ul>
        <li>The misconception that AI will 'take jobs' — the real challenge is our lack of preparedness for new roles that AI will create</li>
        <li>The rapid pace of technological change makes it difficult to predict what will be replaced or created</li>
        <li>Growing inequality caused by lack of internet access, infrastructures, and energy in many African countries</li>
      </ul>
      <h2>Day Two: Business Day at Station F1</h2>
      <p>On February 11, the Business Day at Station F1 brought together over 800 European startups, showcasing innovative projects and fostering meaningful conversations about the future of AI-driven solutions.</p>
      <p>This experience has further strengthened our mission with AgriHyphen AI — to bring AI-powered solutions to African farmers, reducing crop losses and transforming agriculture. Africa must not be left behind in the AI revolution.</p>
    `,
    date: "February 11, 2025",
    category: "Events",
    readTime: "5 min read",
  },
  {
    id: 2,
    title: "How AI Will Enable Agriculture to Produce Better in Africa",
    excerpt:
      "Exploring the transformative potential of AI in African agriculture at the French Institute of Burundi conference.",
    image:
      "https://hyphentech.africa/static/media/ifb-audience.fa6af4d27f77e79d6e8a.png",
    content: `
      <p>We were honored to speak at the French Institute of Burundi on February 3, 2025, during a conference organized by the French Embassy and Campus France Burundi under the theme: 'The Role of Artificial Intelligence in the Jobs of the Future'.</p>
      <h2>The Challenge</h2>
      <p>Africa's population is growing rapidly, yet we remain heavily dependent on food imports. In Burundi:</p>
      <ul>
        <li>Population doubled from 6 million (2000) to 13 million (2025)</li>
        <li>Projected to reach 25 million by 2050</li>
        <li>84% of the population are farmers</li>
        <li>Agriculture contributes 39.6% to GDP</li>
        <li>Farmers lose 40–50% of crops to diseases and pests</li>
      </ul>
      <p>Eliminating the 40% waste could add $850 million to Burundi's GDP annually.</p>
      <h2>AgriHyphen AI Solution</h2>
      <p>At AgriHyphen AI, we're pioneering AI-powered solutions to reduce crop waste, improve yields, and ensure food security. Our model detects 37 plant diseases with 93.8% accuracy.</p>
    `,
    date: "February 03, 2025",
    category: "Research",
    readTime: "4 min read",
  },
  {
    id: 3,
    title: "AgriHyphen AI Wins 8.5 Million BIF at Innovation Week",
    excerpt:
      "Recognition as one of the Top 10 Innovators of the Year at the Innovation Week organized by PNUD Burundi.",
    image:
      "https://hyphentech.africa/static/media/innovation-week.243af1899dcdd634e3f6.jpg",
    content: `
      <p>We are thrilled to announce that AgriHyphen AI has been recognized as one of the Top 10 Innovators of the Year at the prestigious Innovation Week, organized by PNUD Burundi in partnership with PAEEJ.</p>
      <h2>A Remarkable Achievement</h2>
      <ul>
        <li>Selected from over 1,100 innovative projects across the country</li>
        <li>Awarded 8.5 million Burundian Francs</li>
        <li>Pitched at the Presidential Palace in Kiriri, Bujumbura</li>
      </ul>
      <p>As we celebrate this achievement, we remain focused on our mission to revolutionize agriculture through AI. This is just the beginning of a larger movement to empower farmers, reduce crop losses, and enhance food security across Burundi and Africa.</p>
    `,
    date: "December 15, 2024",
    category: "Awards",
    readTime: "3 min read",
  },
];

const Byline = ({ blog, light = false }) => (
  <p
    className={`flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[0.6875rem] uppercase tracking-[0.12em] ${
      light ? "text-white/60" : "text-agri-ink40"
    }`}
  >
    <span className={light ? "text-agri-lime" : "text-agri-clay"}>
      {blog.category}
    </span>
    <span aria-hidden="true">·</span>
    <span>{blog.date}</span>
    <span aria-hidden="true">·</span>
    <span>{blog.readTime}</span>
  </p>
);

const BlogDetail = ({ blog, onBack, t }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <article className="bg-agri-paper">
      <header className="border-b border-agri-rule bg-agri-card">
        <div className="container-x page-top pb-12">
          <button
            onClick={onBack}
            className="link-rule mb-10 text-agri-ink60 hover:text-agri-clay"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("Back to blogs")}
          </button>

          <Byline blog={blog} />
          <h1 className="mt-5 max-w-[20ch] text-balance font-display text-h1 font-semibold leading-[1.02] tracking-[-0.03em] text-agri-ink sm:text-h1Lg">
            {t(blog.title)}
          </h1>
        </div>
      </header>

      <figure className="bg-agri-forestDeep">
        <img
          src={blog.image}
          alt={blog.title}
          className="h-[16rem] w-full object-cover sm:h-[24rem] lg:h-[32rem]"
        />
      </figure>

      <div className="container-x section-tight">
        <div className="grid grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-12">
          {/* Standfirst pulled into the left rail, body at reading measure */}
          <aside className="lg:col-span-3">
            <p className="border-t border-agri-rule pt-4 text-[0.9375rem] italic leading-[1.65] text-agri-ink60">
              {t(blog.excerpt)}
            </p>
          </aside>

          <div
            className="article lg:col-span-8 lg:col-start-5"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
        </div>

        <div className="mt-16 border-t border-agri-rule pt-8">
          <button onClick={onBack} className="btn-outline">
            <ArrowLeft className="h-4 w-4" />
            {t("Back to blogs")}
          </button>
        </div>
      </div>
    </article>
  );
};

const Blogs = () => {
  const { t } = useTranslation();
  const [selectedBlog, setSelectedBlog] = useState(null);

  const open = (blog) => {
    setSelectedBlog(blog);
    window.scrollTo(0, 0);
  };
  const back = () => {
    setSelectedBlog(null);
    window.scrollTo(0, 0);
  };

  if (selectedBlog) {
    return <BlogDetail blog={selectedBlog} onBack={back} t={t} />;
  }

  const [featured, ...rest] = blogPosts;

  return (
    <main className="bg-agri-paper">
      <PageHeader
        kicker="Dispatches"
        title={t("Blogs")}
        lede={t("Latest news and updates from AgriHyphen AI")}
      />

      <div className="container-x section-tight">
        {/* Lead story. The trigger is a real button on the headline, stretched
            over the card — a <button> may not legally wrap a heading or image,
            and this keeps the accessible name to the article title. */}
        <article className="group relative grid grid-cols-1 gap-x-12 gap-y-6 lg:grid-cols-12 lg:items-center">
          <div className="overflow-hidden bg-agri-paperDeep lg:col-span-7">
            <img
              src={featured.image}
              alt=""
              aria-hidden="true"
              className="aspect-[16/10] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]"
            />
          </div>
          <div className="lg:col-span-5">
            <Byline blog={featured} />
            <h2 className="mt-4 text-balance font-display text-[1.75rem] font-semibold leading-[1.12] tracking-[-0.024em] text-agri-ink sm:text-[2.25rem]">
              <button
                onClick={() => open(featured)}
                className="text-left transition-colors after:absolute after:inset-0 hover:text-agri-clay"
              >
                {t(featured.title)}
              </button>
            </h2>
            <p className="mt-4 max-w-measure text-[0.9375rem] leading-[1.7] text-agri-ink80">
              {t(featured.excerpt)}
            </p>
            <p className="mt-6 inline-flex items-center gap-2 font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-agri-ink">
              Read article
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </p>
          </div>
        </article>

        {/* Remaining stories as ruled rows */}
        <ul className="mt-16 border-t border-agri-rule">
          {rest.map((blog) => (
            <li key={blog.id}>
              <article className="group relative grid grid-cols-1 gap-x-10 gap-y-4 border-b border-agri-rule py-8 transition-colors duration-300 hover:bg-agri-card sm:grid-cols-12 sm:items-center lg:px-4">
                <div className="overflow-hidden bg-agri-paperDeep sm:col-span-3">
                  <img
                    src={blog.image}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover"
                  />
                </div>
                <div className="sm:col-span-7">
                  <Byline blog={blog} />
                  <h2 className="mt-3 text-balance font-display text-[1.25rem] font-semibold leading-[1.2] tracking-[-0.018em] text-agri-ink sm:text-[1.5rem]">
                    <button
                      onClick={() => open(blog)}
                      className="text-left transition-colors after:absolute after:inset-0 hover:text-agri-clay"
                    >
                      {t(blog.title)}
                    </button>
                  </h2>
                  <p className="mt-2.5 max-w-measure text-[0.9375rem] leading-[1.7] text-agri-ink80">
                    {t(blog.excerpt)}
                  </p>
                </div>
                <p className="flex items-center gap-2 font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-agri-ink60 transition-colors group-hover:text-agri-ink sm:col-span-2 sm:justify-end">
                  Read
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </p>
              </article>
            </li>
          ))}
        </ul>
      </div>
    </main>
  );
};

export default Blogs;
